import type { InventoryItem, Warehouse } from '../types/inventory.types';

interface UseBulkOperationsHandlersProps {
  companyId: string | null;
  inventoryItems: InventoryItem[];
  warehouses: Warehouse[];
  selectedItems: string[];
  bulkOps: any; // useBulkOperations hook'undan gelen tüm fonksiyonlar
  showSnackbar: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
  setError: (error: string | null) => void;
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
  setInventoryItems: React.Dispatch<React.SetStateAction<InventoryItem[]>>;
  handleRefreshInventory: () => Promise<void>;
}

export const useBulkOperationsHandlers = ({
  companyId,
  inventoryItems,
  warehouses,
  selectedItems,
  bulkOps,
  showSnackbar,
  setError,
  setSelectedItems,
  setInventoryItems,
  handleRefreshInventory,
}: UseBulkOperationsHandlersProps) => {
  
  // ============== BULK DELETE ==============
  
  const handleBulkDelete = () => {
    if (selectedItems.length > 0) {
      bulkOps.setShowBulkDeleteDialog(true);
    } else {
      setError('Lütfen silmek için en az bir ürün seçin');
    }
  };

  const confirmBulkDelete = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkDeleteDialog(false);
      return;
    }

    try {
      await bulkOps.executeBulkDelete(selectedItems);
      
      setInventoryItems(prevItems => 
        prevItems.filter(item => !selectedItems.includes(item.id))
      );
      
      bulkOps.setShowBulkDeleteDialog(false);
      setSelectedItems([]);
      setError(null);
      
      showSnackbar(`${selectedItems.length} ürün başarıyla silindi`, 'success');
      
      await new Promise(resolve => setTimeout(resolve, 500));
      await handleRefreshInventory();
    } catch (error) {
      console.error("Toplu silme sırasında hata oluştu:", error);
      setError('Toplu silme işlemi sırasında bir hata oluştu');
      showSnackbar('Toplu silme işlemi sırasında bir hata oluştu', 'error');
    }
  };

  const cancelBulkDelete = () => {
    bulkOps.setShowBulkDeleteDialog(false);
  };

  // ============== BULK INACTIVATE ==============
  
  const handleBulkInactivate = () => {
    if (selectedItems.length > 0) {
      bulkOps.setShowBulkInactivateDialog(true);
    } else {
      setError('Lütfen pasife almak için en az bir ürün seçin');
    }
  };

  const confirmBulkInactivate = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkInactivateDialog(false);
      return;
    }

    try {
      await bulkOps.executeBulkInactivate(selectedItems);
      
      setInventoryItems(prevItems =>
        prevItems.map(item =>
          selectedItems.includes(item.id) ? { ...item, isActive: false } : item
        )
      );
      
      bulkOps.setShowBulkInactivateDialog(false);
      setSelectedItems([]);
      setError(null);
      
      showSnackbar(`${selectedItems.length} ürün pasife alındı`, 'success');
      
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
    } catch (error) {
      console.error("Toplu pasife alma sırasında hata oluştu:", error);
      setError('Toplu pasife alma işlemi sırasında bir hata oluştu');
      showSnackbar('Toplu pasife alma işlemi sırasında bir hata oluştu', 'error');
    }
  };

  const cancelBulkInactivate = () => {
    bulkOps.setShowBulkInactivateDialog(false);
  };

  // ============== BULK PRICE UPDATE ==============
  
  const handleBulkPriceUpdate = () => {
    if (selectedItems.length > 0) {
      bulkOps.setPriceUpdateTarget('sale');
      bulkOps.setShowBulkPriceUpdateDialog(true);
    } else {
      setError('Lütfen fiyat güncellemesi için en az bir ürün seçin');
    }
  };

  const handleBulkPurchasePriceUpdate = () => {
    if (selectedItems.length > 0) {
      bulkOps.setPriceUpdateTarget('purchase');
      bulkOps.setShowBulkPriceUpdateDialog(true);
    } else {
      setError('Lütfen fiyat güncellemesi için en az bir ürün seçin');
    }
  };

  const confirmBulkPriceUpdate = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkPriceUpdateDialog(false);
      return;
    }

    try {
      await bulkOps.executeBulkPriceUpdate(selectedItems);
      
      setInventoryItems(prevItems =>
        prevItems.map(item => {
          if (!selectedItems.includes(item.id)) return item;
          
          let newPrice: number;
          
          if (bulkOps.priceUpdateTarget === 'sale') {
            if (bulkOps.priceUpdateType === 'percentage') {
              newPrice = item.salePrice * (1 + bulkOps.priceUpdateValue / 100);
            } else {
              newPrice = item.salePrice + bulkOps.priceUpdateValue;
            }
            newPrice = Math.max(0, Math.round(newPrice * 100) / 100);
            return { ...item, salePrice: newPrice };
          } else {
            if (bulkOps.priceUpdateType === 'percentage') {
              newPrice = item.purchasePrice * (1 + bulkOps.priceUpdateValue / 100);
            } else {
              newPrice = item.purchasePrice + bulkOps.priceUpdateValue;
            }
            newPrice = Math.max(0, Math.round(newPrice * 100) / 100);
            return { ...item, purchasePrice: newPrice };
          }
        })
      );
      
      bulkOps.setShowBulkPriceUpdateDialog(false);
      setSelectedItems([]);
      setError(null);
      
      showSnackbar(`${selectedItems.length} ürün fiyatı başarıyla güncellendi`, 'success');
      
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
    } catch (error) {
      console.error("Toplu fiyat güncelleme sırasında hata oluştu:", error);
      setError('Toplu fiyat güncelleme işlemi sırasında bir hata oluştu');
      showSnackbar('Toplu fiyat güncelleme işlemi sırasında bir hata oluştu', 'error');
    }
  };

  const cancelBulkPriceUpdate = () => {
    bulkOps.setShowBulkPriceUpdateDialog(false);
  };

  // ============== BULK ECOMMERCE ==============
  
  const handleBulkOpenForEcommerce = () => {
    if (selectedItems.length > 0) {
      bulkOps.setBulkEcommerceAction('open');
      bulkOps.setShowBulkEcommerceDialog(true);
    } else {
      setError('Lütfen e-ticarete açmak için en az bir ürün seçin');
    }
  };

  const handleBulkCloseForEcommerce = () => {
    if (selectedItems.length > 0) {
      bulkOps.setBulkEcommerceAction('close');
      bulkOps.setShowBulkEcommerceDialog(true);
    } else {
      setError('Lütfen e-ticaretten kapatmak için en az bir ürün seçin');
    }
  };

  const confirmBulkEcommerceAction = async () => {
    if (!companyId || selectedItems.length === 0 || !bulkOps.bulkEcommerceAction) {
      bulkOps.setShowBulkEcommerceDialog(false);
      return;
    }

    try {
      await bulkOps.executeBulkEcommerce(selectedItems, bulkOps.bulkEcommerceAction);
      
      const enable = bulkOps.bulkEcommerceAction === 'open';
      setInventoryItems(prevItems =>
        prevItems.map(item =>
          selectedItems.includes(item.id)
            ? { ...item, isEcommerce: enable }
            : item
        )
      );
      
      bulkOps.setShowBulkEcommerceDialog(false);
      setSelectedItems([]);
      bulkOps.setBulkEcommerceAction(null);
      setError(null);
      
      const actionText = enable ? 'e-ticarete açıldı' : 'e-ticaretten kapatıldı';
      showSnackbar(`${selectedItems.length} ürün başarıyla ${actionText}`, 'success');
      
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
    } catch (error) {
      console.error("Toplu e-ticaret işlemi sırasında hata oluştu:", error);
      setError('Toplu e-ticaret işlemi sırasında bir hata oluştu');
      showSnackbar('Toplu e-ticaret işlemi sırasında bir hata oluştu', 'error');
    }
  };

  const cancelBulkEcommerceAction = () => {
    bulkOps.setShowBulkEcommerceDialog(false);
    bulkOps.setBulkEcommerceAction(null);
  };

  // ============== BULK DELETE IMAGES ==============
  
  const handleBulkDeleteImages = () => {
    if (selectedItems.length > 0) {
      bulkOps.setShowBulkDeleteImagesDialog(true);
    } else {
      setError('Lütfen resimlerini silmek için en az bir ürün seçin');
    }
  };

  const confirmBulkDeleteImages = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkDeleteImagesDialog(false);
      return;
    }

    try {
      await bulkOps.executeBulkDeleteImages(selectedItems);
      
      bulkOps.setShowBulkDeleteImagesDialog(false);
      
      setInventoryItems(prevItems =>
        prevItems.map(item =>
          selectedItems.includes(item.id) ? { ...item, imageUrl: null } : item
        )
      );
      
      setSelectedItems([]);
      setError(null);
    } catch (error) {
      console.error("Toplu resim silme sırasında hata oluştu:", error);
      setError('Toplu resim silme işlemi sırasında bir hata oluştu');
    }
  };

  const cancelBulkDeleteImages = () => {
    bulkOps.setShowBulkDeleteImagesDialog(false);
  };

  // ============== BULK QR MENU ==============
  
  const handleBulkToggleQRMenu = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Lütfen QR Menü\'de açmak için en az bir ürün seçin', 'warning');
      return;
    }
    
    const selectedProducts = inventoryItems.filter(item => selectedItems.includes(item.id));
    const alreadyOpenItems = selectedProducts.filter(item => item.isQRMenu === true);
    const closedItems = selectedProducts.filter(item => item.isQRMenu !== true);
    
    if (closedItems.length === 0) {
      showSnackbar('Seçili tüm ürünler zaten QR Menü\'de açık!', 'info');
      return;
    }
    
    bulkOps.setBulkQRMenuResults({
      success: 0, 
      failed: 0, 
      total: closedItems.length,
      alreadyOpen: alreadyOpenItems.length,
      alreadyOpenNames: alreadyOpenItems.map(i => i.name)
    });
    bulkOps.setBulkQRMenuProgress(0);
    bulkOps.setBulkQRMenuCurrentItem('');
    bulkOps.setShowBulkQRMenuModal(true);
  };

  const confirmBulkToggleQRMenu = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkQRMenuModal(false);
      return;
    }

    try {
      const result = await bulkOps.executeBulkToggleQRMenu(
        selectedItems,
        inventoryItems,
        (itemId: string, updates: Partial<InventoryItem>) => {
          setInventoryItems(prevItems =>
            prevItems.map(i => (i.id === itemId ? { ...i, ...updates } : i))
          );
        }
      );
      
      if (result.success > 0) {
        showSnackbar(`${result.success} ürün başarıyla QR Menü'de açıldı!`, 'success');
      }
      
      if (result.failed > 0) {
        showSnackbar(`${result.failed} ürün işlenirken hata oluştu`, 'warning');
      }

      setSelectedItems([]);
      
      setTimeout(() => {
        bulkOps.setShowBulkQRMenuModal(false);
        bulkOps.setBulkQRMenuProgress(0);
        bulkOps.setBulkQRMenuResults({success: 0, failed: 0, total: 0});
        bulkOps.setBulkQRMenuCurrentItem('');
      }, 500);
      
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
      
    } catch (error) {
      console.error('❌ [BULK QR MENU TOGGLE] Toplu açma hatası:', error);
      showSnackbar(error instanceof Error ? error.message : 'Toplu QR Menü açma sırasında bir hata oluştu', 'error');
    }
  };

  const cancelBulkToggleQRMenu = () => {
    if (!bulkOps.bulkQRMenuProcessing) {
      bulkOps.setShowBulkQRMenuModal(false);
      bulkOps.setBulkQRMenuProgress(0);
      bulkOps.setBulkQRMenuResults({success: 0, failed: 0, total: 0});
      bulkOps.setBulkQRMenuCurrentItem('');
    }
  };

  // ============== BULK KIOSK ==============
  
  const handleBulkToggleKiosk = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Lütfen Kiosk\'ta açmak için en az bir ürün seçin', 'warning');
      return;
    }
    
    const selectedProducts = inventoryItems.filter(item => selectedItems.includes(item.id));
    const alreadyOpenItems = selectedProducts.filter(item => item.isKiosk === true);
    const closedItems = selectedProducts.filter(item => item.isKiosk !== true);
    
    if (closedItems.length === 0) {
      showSnackbar('Seçili tüm ürünler zaten Kiosk\'ta açık!', 'info');
      return;
    }
    
    bulkOps.setBulkKioskResults({
      success: 0, 
      failed: 0, 
      total: closedItems.length,
      alreadyOpen: alreadyOpenItems.length,
      alreadyOpenNames: alreadyOpenItems.map(i => i.name)
    });
    bulkOps.setBulkKioskProgress(0);
    bulkOps.setBulkKioskCurrentItem('');
    bulkOps.setShowBulkKioskModal(true);
  };

  const confirmBulkToggleKiosk = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkKioskModal(false);
      return;
    }

    try {
      const result = await bulkOps.executeBulkToggleKiosk(
        selectedItems,
        inventoryItems,
        (itemId: string, updates: Partial<InventoryItem>) => {
          setInventoryItems(prevItems =>
            prevItems.map(i => (i.id === itemId ? { ...i, ...updates } : i))
          );
        }
      );
      
      if (result.success > 0) {
        showSnackbar(`${result.success} ürün başarıyla Kiosk'ta açıldı!`, 'success');
      }
      
      if (result.failed > 0) {
        showSnackbar(`${result.failed} ürün işlenirken hata oluştu`, 'warning');
      }

      setSelectedItems([]);
      
      setTimeout(() => {
        bulkOps.setShowBulkKioskModal(false);
        bulkOps.setBulkKioskProgress(0);
        bulkOps.setBulkKioskResults({success: 0, failed: 0, total: 0});
        bulkOps.setBulkKioskCurrentItem('');
      }, 500);
      
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
      
    } catch (error) {
      console.error('❌ [BULK KIOSK TOGGLE] Toplu açma hatası:', error);
      showSnackbar(error instanceof Error ? error.message : 'Toplu Kiosk açma sırasında bir hata oluştu', 'error');
    }
  };

  const cancelBulkToggleKiosk = () => {
    if (!bulkOps.bulkKioskProcessing) {
      bulkOps.setShowBulkKioskModal(false);
      bulkOps.setBulkKioskProgress(0);
      bulkOps.setBulkKioskResults({success: 0, failed: 0, total: 0});
      bulkOps.setBulkKioskCurrentItem('');
    }
  };

  // ============== BULK WAREHOUSE ASSIGN ==============
  
  const handleBulkAssignWarehouse = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Lütfen depoya atamak için en az bir ürün seçin', 'warning');
      return;
    }
    
    const itemsToAssign = inventoryItems.filter(item => 
      selectedItems.includes(item.id) && 
      (!item.warehouseStocks || item.warehouseStocks.length === 0)
    );
    
    if (itemsToAssign.length === 0) {
      showSnackbar('Seçili tüm ürünlerin zaten deposu var!', 'info');
      return;
    }
    
    if (itemsToAssign.length < selectedItems.length) {
      showSnackbar(
        `${selectedItems.length - itemsToAssign.length} ürünün zaten deposu var. Sadece ${itemsToAssign.length} ürün atanacak.`,
        'info'
      );
    }
    
    bulkOps.setShowBulkWarehouseAssignModal(true);
  };

  const confirmBulkAssignWarehouse = async (warehouseId: string) => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkWarehouseAssignModal(false);
      return;
    }

    try {
      const itemsToAssign = inventoryItems.filter(item => 
        selectedItems.includes(item.id) && 
        (!item.warehouseStocks || item.warehouseStocks.length === 0)
      );
      
      if (itemsToAssign.length === 0) {
        showSnackbar('Atanacak deposuz ürün bulunamadı', 'warning');
        bulkOps.setShowBulkWarehouseAssignModal(false);
        return;
      }
      
      const result = await bulkOps.executeBulkAssignWarehouse(
        itemsToAssign.map(item => item.id),
        warehouseId
      );
      
      setInventoryItems(prevItems =>
        prevItems.map(item =>
          itemsToAssign.some(assignedItem => assignedItem.id === item.id)
            ? {
                ...item,
                warehouseStocks: [{
                  id: '',
                  productId: item.id,
                  warehouseId: warehouseId,
                  warehouseName: warehouses.find(w => w.id === warehouseId)?.name,
                  quantity: 0,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                }]
              }
            : item
        )
      );
      
      showSnackbar(`${result.count} ürün başarıyla depoya atandı!`, 'success');
      
      setSelectedItems([]);
      bulkOps.setShowBulkWarehouseAssignModal(false);
      
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
      
    } catch (error) {
      console.error('❌ [BULK ASSIGN WAREHOUSE] Hata:', error);
      showSnackbar(error instanceof Error ? error.message : 'Toplu depo atama sırasında bir hata oluştu', 'error');
    }
  };

  const cancelBulkAssignWarehouse = () => {
    if (!bulkOps.bulkWarehouseAssignProcessing) {
      bulkOps.setShowBulkWarehouseAssignModal(false);
    }
  };

  // ============== BULK VAT RATE ==============
  
  const handleBulkUpdateVatRate = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Lütfen KDV oranını güncellemek için en az bir ürün seçin', 'warning');
      return;
    }
    bulkOps.setShowBulkVatRateDialog(true);
  };

  const confirmBulkUpdateVatRate = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkVatRateDialog(false);
      return;
    }

    try {
      await bulkOps.executeBulkVatRateUpdate(selectedItems);
      
      setInventoryItems(prevItems =>
        prevItems.map(item =>
          selectedItems.includes(item.id)
            ? { ...item, vatRate: bulkOps.bulkVatRate }
            : item
        )
      );
      
      bulkOps.setShowBulkVatRateDialog(false);
      setSelectedItems([]);
      
      showSnackbar(`${selectedItems.length} ürünün KDV oranı %${bulkOps.bulkVatRate} olarak güncellendi`, 'success');
      
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
    } catch (error) {
      console.error("Toplu KDV güncelleme sırasında hata oluştu:", error);
      showSnackbar('Toplu KDV güncelleme işlemi sırasında bir hata oluştu', 'error');
    }
  };

  const cancelBulkUpdateVatRate = () => {
    bulkOps.setShowBulkVatRateDialog(false);
  };

  // ============== BULK DEPARTMENT NO ==============
  
  const handleBulkUpdateDepartmentNo = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Lütfen ÖKC değerini güncellemek için en az bir ürün seçin', 'warning');
      return;
    }
    bulkOps.setShowBulkDepartmentNoDialog(true);
  };

  const confirmBulkUpdateDepartmentNo = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkDepartmentNoDialog(false);
      return;
    }

    try {
      await bulkOps.executeBulkDepartmentNoUpdate(selectedItems);
      
      setInventoryItems(prevItems =>
        prevItems.map(item =>
          selectedItems.includes(item.id)
            ? { ...item, departmentNo: bulkOps.bulkDepartmentNo }
            : item
        )
      );
      
      bulkOps.setShowBulkDepartmentNoDialog(false);
      setSelectedItems([]);
      
      showSnackbar(`${selectedItems.length} ürünün ÖKC değeri ${bulkOps.bulkDepartmentNo} olarak güncellendi`, 'success');
      
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
    } catch (error) {
      console.error("Toplu ÖKC güncelleme sırasında hata oluştu:", error);
      showSnackbar('Toplu ÖKC güncelleme işlemi sırasında bir hata oluştu', 'error');
    }
  };

  const cancelBulkUpdateDepartmentNo = () => {
    bulkOps.setShowBulkDepartmentNoDialog(false);
  };

  // ============== BULK VAT + DEPT ==============
  
  const handleBulkUpdateVatAndDept = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Lütfen KDV ve ÖKC değerlerini güncellemek için en az bir ürün seçin', 'warning');
      return;
    }
    bulkOps.setShowBulkVatAndDeptDialog(true);
  };

  const confirmBulkUpdateVatAndDept = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkVatAndDeptDialog(false);
      return;
    }

    try {
      await bulkOps.executeBulkVatAndDeptUpdate(selectedItems);
      
      setInventoryItems(prevItems =>
        prevItems.map(item =>
          selectedItems.includes(item.id)
            ? { ...item, vatRate: bulkOps.bulkVatRate, departmentNo: bulkOps.bulkDepartmentNo }
            : item
        )
      );
      
      bulkOps.setShowBulkVatAndDeptDialog(false);
      setSelectedItems([]);
      
      showSnackbar(`${selectedItems.length} ürünün KDV oranı %${bulkOps.bulkVatRate} ve ÖKC değeri ${bulkOps.bulkDepartmentNo} olarak güncellendi`, 'success');
      
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
    } catch (error) {
      console.error("Toplu KDV+ÖKC güncelleme sırasında hata oluştu:", error);
      showSnackbar('Toplu KDV+ÖKC güncelleme işlemi sırasında bir hata oluştu', 'error');
    }
  };

  const cancelBulkUpdateVatAndDept = () => {
    bulkOps.setShowBulkVatAndDeptDialog(false);
  };

  // ============== RETURN ALL HANDLERS ==============
  
  return {
    // Delete
    handleBulkDelete,
    confirmBulkDelete,
    cancelBulkDelete,
    
    // Inactivate
    handleBulkInactivate,
    confirmBulkInactivate,
    cancelBulkInactivate,
    
    // Price Update
    handleBulkPriceUpdate,
    handleBulkPurchasePriceUpdate,
    confirmBulkPriceUpdate,
    cancelBulkPriceUpdate,
    
    // Ecommerce
    handleBulkOpenForEcommerce,
    handleBulkCloseForEcommerce,
    confirmBulkEcommerceAction,
    cancelBulkEcommerceAction,
    
    // Images
    handleBulkDeleteImages,
    confirmBulkDeleteImages,
    cancelBulkDeleteImages,
    
    // QR Menu
    handleBulkToggleQRMenu,
    confirmBulkToggleQRMenu,
    cancelBulkToggleQRMenu,
    
    // Kiosk
    handleBulkToggleKiosk,
    confirmBulkToggleKiosk,
    cancelBulkToggleKiosk,
    
    // Warehouse
    handleBulkAssignWarehouse,
    confirmBulkAssignWarehouse,
    cancelBulkAssignWarehouse,
    
    // VAT Rate
    handleBulkUpdateVatRate,
    confirmBulkUpdateVatRate,
    cancelBulkUpdateVatRate,
    
    // Department No
    handleBulkUpdateDepartmentNo,
    confirmBulkUpdateDepartmentNo,
    cancelBulkUpdateDepartmentNo,
    
    // VAT + Dept
    handleBulkUpdateVatAndDept,
    confirmBulkUpdateVatAndDept,
    cancelBulkUpdateVatAndDept,
  };
};

